import type { ApiClient } from '../client';

// Who the parcel becomes, and who the agent believed it belonged to when they read the card.
export interface RecipientChange {
  // The «Usuario» resolved from the phone the agent typed, by the same read the header search uses.
  // An ID and never the number: the backend cannot then resolve to somebody other than the one the
  // agent saw, and no «Usuario» PII reaches the request log of this route.
  userId: number;
  // The concurrency guard, and the reason this write is unlike the nine before it. Every sibling is
  // idempotent in intention — «put this one in `collected`» means the same whoever asked first —
  // whereas this one's correctness depends on a value read minutes ago: two agents on the same
  // «Entrega» and the second write silently overwrites the first, wiping the authorized pickups a
  // second time on its way. Backend compares it under `SELECT … FOR UPDATE`, so the compare-and-set
  // is real and not a race between two reads.
  //
  // `null` is a VALUE here, not an absence: it asserts «the card showed no titular», which is a
  // claim about the «Entrega» exactly as an id is, and `recipient: null` is a real case of the
  // model. Backend made the key mandatory-and-nullable to keep it that way — omitting it answers
  // 400 rather than skipping the guard in silence.
  expectedCurrentUserId: number | null;
}

// Reassign a «Entrega» to another «Usuario» — «Cambiar titular», the panel's tenth WRITE and its
// eighth on a «Entrega». The id rides the path (url-encoded); the client attaches the Firebase
// token via the Authorization header. Empty 200 — nothing to parse; the caller refetches the detail
// and the repainted titular IS the effect.
//
// The path names the ACTION rather than the datum. It is what the six sibling writes do
// (`/collect`, `/extend-expiration`…) and what the admin route doing this same job does: a lone
// `/recipient` would read as a sub-resource with a GET behind it, and there is none — the titular
// already travels in the detail this refetches.
//
// Shape CONFIRMED — [AS-056], delivered by backend on 2026-09-15 (CBX-11409, app-service ADR-0051).
// Three effects the panel asks for and does not compose: the write moves BOTH the modular
// `deliveries.recipient` and the legacy `pedidos.usuario` in one transaction (so the outgoing
// titular loses access, because that column is what BLE opening validates), it revokes EVERY share
// of the «Entrega» as a second command inside that same transaction and AFTER the guards, and it
// notifies nobody — not the outgoing titular, not the incoming one, not the authorized pickups it
// removes.
export async function changeDeliveryRecipient(
  client: ApiClient,
  id: string,
  change: RecipientChange,
): Promise<void> {
  // Composed key by key rather than spread: a spread of the argument would carry whatever a caller
  // put in it, and `exactOptionalPropertyTypes` does not stop an `as never` at the call site. Two
  // ids leave this function, which is the whole of what the contract asks for.
  const body = {
    user_id: change.userId,
    expected_current_user_id: change.expectedCurrentUserId,
  };

  await client.post(
    `/app_service/panel/v1/deliveries/${encodeURIComponent(id)}/change-recipient`,
    body,
  );
}
